import type { DrawingSet, Pt, StrokeWeight } from '@/lib/drawings/types'

import {
    iso,
    isoBox,
    levelLabel,
    mm,
    Sheet
} from '@/lib/drawings/types'

const LETTERS = 'ABCDEFGH'

const range = (from: number, to: number, step: number): Array<number> => {
    const values: Array<number> = []

    for (let v = from; v <= to + 1e-6; v += step) values.push(Number(v.toFixed(4)))

    return values
}

const gridX = (sheet: Sheet, xs: Array<number>, y0: number, y1: number): void => {
    xs.forEach((x, index) => sheet.grid([x, y0], [x, y1], String(index + 1)))
}

/**
 * Horizontal grid lines with their letter bubble at `bubbleX`, running across to `endX`.
 */
const gridY = (sheet: Sheet, ys: Array<number>, bubbleX: number, endX: number): void => {
    ys.forEach((y, index) => sheet.grid([endX, y], [bubbleX, y], LETTERS[index]))
}

const levels = (sheet: Sheet, x: number, heights: Array<number>): void => {
    heights.forEach(h => sheet.level([x, h], levelLabel(h)))
}

const stair = (sheet: Sheet, x0: number, y0: number, x1: number, y1: number): void => {
    sheet.rect(x0, y0, x1, y1, 'line')

    for (let y = y0 + 0.3; y < y1 - 0.05; y += 0.3) sheet.line([x0, y], [x1, y], 'thin')

    sheet.line([(x0 + x1) / 2, y0], [(x0 + x1) / 2, y1], 'thin')
}

/**
 * Clips a family of parallel diagonal lines to a rectangle; used for gridshells and board marks.
 */
const diagonals = (sheet: Sheet, x0: number, y0: number, x1: number, y1: number, spacing: number, slope: number, weight: StrokeWeight, splitX?: number, after?: StrokeWeight): void => {
    const span = (x1 - x0) + (y1 - y0) * Math.abs(1 / slope)

    for (let c = -span; c <= span; c += spacing) {
        const points: Array<Pt> = []

        for (const x of [x0, x1]) {
            const y = slope * (x - x0) + y0 + c

            if (y >= y0 && y <= y1) points.push([x, y])
        }

        for (const y of [y0, y1]) {
            const x = (y - y0 - c) / slope + x0

            if (x > x0 && x < x1) points.push([x, y])
        }

        if (points.length < 2) continue

        points.sort((a, b) => a[0] - b[0])

        const [a, b] = points

        if (splitX !== undefined && after && splitX > a[0] && splitX < b[0]) {
            const ys = a[1] + (b[1] - a[1]) * ((splitX - a[0]) / (b[0] - a[0]))

            sheet.line(a, [splitX, ys], weight)
            sheet.line([splitX, ys], b, after)
        }
        else if (splitX !== undefined && after && a[0] >= splitX) {
            sheet.line(a, b, after)
        }
        else {
            sheet.line(a, b, weight)
        }
    }
}

/* ----------------------------------------------------------------- library */

const library = (): DrawingSet => {
    const plan = new Sheet('1:300')

    plan.rect(-18, -9, 18, 9, 'cut').rect(-17.7, -8.7, 17.7, 8.7, 'line')
    gridX(plan, range(-18, 18, 6), -11.5, 12.5)
    gridY(plan, [-9, -3, 3, 9], -21.5, 19)

    for (const x of range(-12, 12, 6)) for (const y of [-3, 3]) plan.rect(x - 0.25, y - 0.25, x + 0.25, y + 0.25, 'cut')

    stair(plan, -17.7, -8.7, -14.2, -4.2)
    stair(plan, 14.2, 4.2, 17.7, 8.7)
    plan.rect(-6, -3, 6, 3, 'hidden').line([-6, -3], [6, 3], 'hidden').line([-6, 3], [6, -3], 'hidden')

    for (const y of range(-7.5, -4.5, 1)) plan.line([7, y], [13, y], 'thin')
    for (const y of range(4.5, 7.5, 1)) plan.line([-13, y], [-7, y], 'thin')

    plan.line([-2, -9], [-2, -10.6], 'line').line([2, -9], [2, -10.6], 'line').line([-2, -10.6], [2, -10.6], 'thin')
    plan.dim([-18, -9], [18, -9], -4.4).dim([-18, -9], [-12, -9], -2.6).dim([18, -9], [18, 9], 3.6)
    plan.note([0, 0.2], 'Atrium / void over', 'middle').note([10, -2], 'Reading room', 'middle').note([-10, 2], 'Children', 'middle').note([0, -11.6], 'Entrance', 'middle')
    plan.north([24, 9])

    const section = new Sheet('1:300')

    section.line([-24, 0], [24, 0], 'thin').rect(-18, -0.6, 18, 0, 'cut')
    section.rect(-18, 3.9, -6, 4.2, 'cut').rect(0, 3.9, 18, 4.2, 'cut')
    section.rect(-18, 8.1, -6, 8.4, 'cut').rect(0, 8.1, 18, 8.4, 'cut')
    section.poly([[-18, 0], [-18, 12], [-6, 12], [-6, 13.6], [6, 13.6], [6, 15.2], [18, 15.2], [18, 0]], 'cut')
    section.poly([[-17.6, 11.6], [-6.4, 11.6], [-6.4, 13.2], [5.6, 13.2], [5.6, 14.8], [17.6, 14.8]], 'line')

    for (const x of [-12, -6, 0, 6, 12]) {
        const top = x < -6 ? 11.6 : x < 6 ? 13.2 : 14.8

        section.line([x, 0], [x, top], 'line')
    }

    section.rect(-6, 12, -5.6, 13.6, 'thin').rect(6, 13.6, 6.4, 15.2, 'thin')
    section.line([0, 8.4], [0, 9.5], 'thin').line([0, 9.5], [6, 9.5], 'thin')
    gridX(section, range(-18, 18, 6), -1.6, 18.4)
    levels(section, 20.5, [0, 4.2, 8.4, 12, 13.6, 15.2])
    section.dim([-18, 0], [-18, 12], -3.2).dim([-18, 0], [-18, 4.2], -1.6).dim([0, 15.2], [18, 15.2], 1.6, '18 000')
    section.note([3, 10.3], 'Mezzanine bay C3', 'middle', 'ink').note([-3, 2], 'Atrium', 'middle').note([-12, 6], 'Level 1', 'middle').note([9, 6], 'Level 1', 'middle')
    section.deviation([3, 8.4])

    const elevation = new Sheet('1:300')

    elevation.line([-22, 0], [22, 0], 'line')
    elevation.poly([[-18, 0], [-18, 12], [-6, 12], [-6, 13.6], [6, 13.6], [6, 15.2], [18, 15.2], [18, 0]], 'cut')

    for (const x of range(-17.4, 17.4, 1.2)) {
        const top = x < -6 ? 11.6 : x < 6 ? 13.2 : 14.8

        elevation.line([x, 0.3], [x, top], 'thin')
    }

    elevation.line([-18, 4.2], [18, 4.2], 'line').line([-18, 8.4], [18, 8.4], 'line')
    elevation.rect(-2.4, 0, 2.4, 3.4, 'line')
    levels(elevation, 24, [0, 4.2, 8.4, 12, 13.6, 15.2])
    elevation.dim([-18, 0], [18, 0], -2.4).dim([18, 0], [18, 15.2], 3.6)
    elevation.note([0, -3.8], 'South elevation / timber fins at 1 200', 'middle')

    const axo = new Sheet('NTS')

    isoBox(axo, -18, -9, -6, 9, 0, 12, 'cut')
    isoBox(axo, -6, -9, 6, 9, 0, 13.6, 'cut')
    isoBox(axo, 6, -9, 18, 9, 0, 15.2, 'cut')

    for (const h of [4.2, 8.4]) {
        axo.line(iso(-18, h, 9), iso(18, h, 9), 'line')
        axo.line(iso(18, h, -9), iso(18, h, 9), 'line')
    }

    for (const x of range(-16.8, 16.8, 2.4)) {
        const top = x < -6 ? 12 : x < 6 ? 13.6 : 15.2

        axo.line(iso(x, 0, 9), iso(x, top, 9), 'thin')
    }

    axo.note(iso(-12, 16, 0), 'Stepped CLT roof', 'middle', 'ink').note(iso(12, -4, 9), 'Glulam frame on a 6 m grid', 'middle')

    return { plan: plan.drawing, section: section.drawing, elevation: elevation.drawing, axonometric: axo.drawing }
}

/* ----------------------------------------------------------------- housing */

const housing = (): DrawingSet => {
    const plan = new Sheet('1:400')

    for (const [y0, y1] of [[7, 19], [-19, -7]]) {
        plan.rect(-24, y0, 24, y1, 'cut').rect(-23.7, y0 + 0.3, 23.7, y1 - 0.3, 'line')

        for (const x of range(-18, 18, 6)) if (x !== 0) plan.line([x, y0 + 0.3], [x, y1 - 0.3], 'thin')

        stair(plan, -2, y0 + 3, 2, y1 - 3)
    }

    for (const x of range(-23.25, 23.25, 1.5)) {
        plan.line([x, 19], [x, 19.9], 'thin')
        plan.line([x, -19], [x, -19.9], 'thin')
    }

    for (const [x, y] of [[-12, 1], [-4, -2], [6, 2], [14, -1]]) plan.line([x - 0.8, y], [x + 0.8, y], 'thin').line([x, y - 0.8], [x, y + 0.8], 'thin')

    gridX(plan, range(-24, 24, 12), -22, 23)
    gridY(plan, [-19, -7, 7, 19], -27.5, 25)
    plan.dim([-24, -19.9], [24, -19.9], -3.2).dim([24, 7], [24, 19], 3).dim([24, -7], [24, 7], 3)
    plan.note([0, 0], 'Courtyard', 'middle', 'ink').note([-12, 13], '32 homes', 'middle').note([-12, -13], '32 homes', 'middle').note([0, 21.4], 'West facade / fins 900 deep', 'middle')
    plan.north([30, 19])

    const section = new Sheet('1:300')

    section.line([-24, 0], [24, 0], 'thin')

    for (const [x0, x1] of [[-19, -7], [7, 19]]) {
        section.poly([[x0, 0], [x0, 18.6], [x1, 18.6], [x1, 0]], 'cut')

        for (const h of range(3, 18, 3)) section.rect(x0, h - 0.25, x1, h, 'cut')

        section.rect(x0, -0.5, x1, 0, 'cut')
    }

    section.line([-19.9, 0.4], [-19.9, 18.2], 'thin').line([19.9, 0.4], [19.9, 18.2], 'thin')

    for (const x of [-3, 2]) section.line([x, 0], [x, 4], 'thin').poly([[x - 1.6, 4], [x, 6.2], [x + 1.6, 4]], 'thin', true)

    levels(section, 22, [0, 3, 6, 9, 12, 15, 18, 18.6])
    section.dim([-19, 0], [-19, 18.6], -3.2).dim([-7, 0], [7, 0], -1.8).dim([7, 0], [19, 0], -1.8)
    section.note([0, 7.4], 'Courtyard', 'middle').note([-13, 9.8], 'Level 3', 'middle').note([13, 9.8], 'Level 3', 'middle')

    const elevation = new Sheet('1:400')

    elevation.line([-28, 0], [28, 0], 'line').rect(-24, 0, 24, 18.6, 'cut')

    for (const h of range(3, 18, 3)) elevation.line([-24, h], [24, h], 'line')

    for (const x of range(-23.25, 23.25, 1.5)) elevation.line([x, 0.2], [x, 18.2], 'thin')

    elevation.rect(-1.5, 0, 1.5, 2.8, 'line')
    levels(elevation, 30, [0, 3, 6, 9, 12, 15, 18.6])
    elevation.dim([-24, 0], [24, 0], -2.6).dim([24, 0], [24, 18.6], 3.4)
    elevation.note([-12, 13.2], 'Fin line, level 4', 'middle', 'ink').note([0, -4], 'West elevation / fins at 1 500', 'middle')
    elevation.deviation([-12, 12])

    const axo = new Sheet('NTS')

    isoBox(axo, -24, 7, 24, 19, 0, 18.6, 'cut')
    isoBox(axo, -24, -19, 24, -7, 0, 18.6, 'cut')

    for (const h of range(3, 18, 3)) {
        axo.line(iso(-24, h, 19), iso(24, h, 19), 'line')
        axo.line(iso(24, h, 7), iso(24, h, 19), 'line')
    }

    for (const x of range(-22.5, 22.5, 3)) axo.line(iso(x, 0, 19), iso(x, 18.6, 19), 'thin')

    axo.note(iso(0, 22, 0), 'Two slabs / 64 homes', 'middle', 'ink')

    return { plan: plan.drawing, section: section.drawing, elevation: elevation.drawing, axonometric: axo.drawing }
}

/* -------------------------------------------------------------------- hall */

const hall = (): DrawingSet => {
    const plan = new Sheet('1:400')

    plan.rect(-21, -14, 21, 14, 'cut').rect(-20.7, -13.7, 20.7, 13.7, 'line')
    plan.line([15, -13.7], [15, 13.7], 'line').rect(-20.7, -8, -13, 8, 'line')

    for (let r = 4; r <= 27; r += 1.4) {
        const points: Array<Pt> = []

        for (let a = -42; a <= 42; a += 3) {
            const x = -13 + r * Math.cos(a * Math.PI / 180)
            const y = r * Math.sin(a * Math.PI / 180)

            if (x < 14.2 && Math.abs(y) < 13) points.push([x, y])
        }

        if (points.length > 1) plan.poly(points, 'thin')
    }

    for (const x of range(-15, 15, 6)) plan.line([x, -14], [x, 14], 'hidden')

    ;[-10, -4, 2, 8].forEach((y, index) => {
        plan.rect(20.7, y, 21.3, y + 2.4, 'line')
        plan.note([22.4, y + 1.2], `D${index + 1}`, 'start', index === 2 ? 'signal' : 'mute')
    })

    gridX(plan, range(-21, 21, 6), -16.5, 17.5)
    gridY(plan, [-14, 0, 14], -24.5, 22)
    plan.dim([-21, -14], [21, -14], -3.6).dim([21, -14], [21, 14], 6.2)
    plan.note([-16.8, 0], 'Stage', 'middle', 'ink').note([2, 0], 'Raked seating / 620', 'middle').note([18, 0], 'Lobby', 'middle')
    plan.north([27, 13])

    const section = new Sheet('1:300')

    section.line([-25, 0], [25, 0], 'thin').rect(-21, -0.5, 21, 0, 'cut')
    section.rect(-21, 0, -13, 1.1, 'cut')

    const steps: Array<Pt> = [[-11, 0]]
    let y = 0

    for (let x = -11; x < 14; x += 0.9) {
        y += 0.22
        steps.push([x, y], [x + 0.9, y])
    }

    steps.push([14.4, y], [14.4, 0])
    section.poly(steps, 'cut')
    section.line([-21, 0], [-21, 16], 'cut').line([21, 0], [21, 16], 'cut').line([15, 0], [15, 6.8], 'line')

    section.line([-21, 16], [21, 16], 'cut').line([-21, 13], [21, 13], 'cut')

    for (let k = 0; k <= 14; k++) {
        const x = -21 + k * 3

        section.line([x, 13], [x, 16], 'line')

        if (k < 14) section.line(k % 2 === 0 ? [x, 13] : [x, 16], k % 2 === 0 ? [x + 3, 16] : [x + 3, 13], 'line')
    }

    levels(section, 27, [0, 1.1, y, 13, 16])
    section.dim([-21, 16], [21, 16], 1.8, '42 000').dim([21, 0], [21, 13], 2.8)
    section.note([-17, 2.4], 'Stage', 'middle').note([1, 6.8], 'Raked seating', 'middle').note([0, 17.6], 'Steel truss / 14 panels at 3 000', 'middle', 'ink').note([0, 11.6], 'Node T7', 'middle', 'ink')
    section.deviation([0, 13])

    const elevation = new Sheet('1:400')

    elevation.line([-25, 0], [25, 0], 'line').rect(-21, 0, 21, 16, 'cut').line([-21, 13], [21, 13], 'line')

    for (let k = 0; k < 14; k++) {
        const x = -21 + k * 3

        elevation.line(k % 2 === 0 ? [x, 13] : [x, 16], k % 2 === 0 ? [x + 3, 16] : [x + 3, 13], 'thin')
    }

    for (const x of range(-19.5, 19.5, 1.5)) elevation.line([x, 0], [x, 4.2], 'thin')

    elevation.line([-21, 4.2], [21, 4.2], 'line')
    ;[-10, -4, 2, 8].forEach(x => elevation.rect(x, 0, x + 2.4, 3, 'line'))
    levels(elevation, 28, [0, 4.2, 13, 16])
    elevation.dim([-21, 0], [21, 0], -2.4).dim([21, 0], [21, 16], 3.6)
    elevation.note([0, -4], 'East elevation / truss expressed', 'middle')

    const axo = new Sheet('NTS')

    isoBox(axo, -21, -14, 21, 14, 0, 13, 'cut')
    isoBox(axo, -21, -14, 21, 14, 13, 16, 'line')

    for (let k = 0; k < 14; k++) {
        const x = -21 + k * 3

        axo.line(iso(x, k % 2 === 0 ? 13 : 16, 14), iso(x + 3, k % 2 === 0 ? 16 : 13, 14), 'thin')
    }

    axo.note(iso(0, 20, 0), 'Truss over a tiered hall', 'middle', 'ink')

    return { plan: plan.drawing, section: section.drawing, elevation: elevation.drawing, axonometric: axo.drawing }
}

/* --------------------------------------------------------------- bathhouse */

const bathhouse = (): DrawingSet => {
    const plan = new Sheet('1:250')

    plan.rect(-15, -10, 15, 10, 'cut').rect(-14.5, -9.5, 14.5, 9.5, 'cut')

    const pools: Array<[number, number, number, number, string]> = [[-10, -6, 2, 2, 'Pool A / 12 × 8'], [5, -6, 11, 0, 'Pool B'], [6, 3, 9, 6, 'Plunge']]

    for (const [x0, y0, x1, y1, label] of pools) {
        plan.rect(x0, y0, x1, y1, 'cut').rect(x0 + 0.3, y0 + 0.3, x1 - 0.3, y1 - 0.3, 'line')

        for (const s of [0.6, 0.9, 1.2]) plan.line([x0 + 0.3, y1 - s], [x0 + 1.6, y1 - s], 'thin')

        plan.note([(x0 + x1) / 2, (y0 + y1) / 2], label, 'middle')
    }

    for (const y of range(-9.5, 9.5, 3.8)) plan.line([-14.5, y], [-12, y], 'thin')

    plan.line([-12, -9.5], [-12, 9.5], 'line').rect(-10, -2.6, 2, -1.4, 'hidden')
    gridX(plan, [-15, -5, 5, 15], -12.5, 13.5)
    gridY(plan, [-10, 0, 10], -18, 16)
    plan.dim([-15, -10], [15, -10], -3).dim([15, -10], [15, 10], 2.6).dim([-10, -6], [2, -6], -1.4)
    plan.note([-13.2, 8.4], 'Changing', 'middle').note([-4, -3.4], 'Roof light over', 'middle')
    plan.north([20, 10])

    const section = new Sheet('1:200')

    section.line([-19, 0], [19, 0], 'thin')
    section.rect(-15, 0, -14.5, 6, 'cut').rect(14.5, 0, 15, 6, 'cut')
    section.rect(-15, 6, -10, 6.6, 'cut').rect(2, 6, 15, 6.6, 'cut')
    section.poly([[-10, 6.6], [-10, 7.2], [2, 7.2], [2, 6.6]], 'line').line([-10, 6.9], [2, 6.9], 'thin')
    section.poly([[-15, 0], [-10, 0], [-10, -1.4], [2, -1.4], [2, 0], [5, 0], [5, -1.8], [11, -1.8], [11, 0], [15, 0]], 'cut')
    section.line([-10, -0.2], [2, -0.2], 'thin').line([5, -0.2], [11, -0.2], 'thin')
    section.poly([[-15, -0.5], [-10.4, -0.5], [-10.4, -1.9], [2.4, -1.9], [2.4, -0.5], [4.6, -0.5], [4.6, -2.3], [11.4, -2.3], [11.4, -0.5], [15, -0.5]], 'line')
    levels(section, 17.5, [-1.8, -1.4, 0, 6, 7.2])
    section.dim([-15, 0], [-15, 6], -2.4).dim([-10, 7.2], [2, 7.2], 1.4)
    section.note([-4, 3], 'Pool A', 'middle').note([8, 3], 'Pool B', 'middle').note([-4, 8.6], 'Roof light', 'middle', 'ink').note([11, -3.2], 'Edge of pool B', 'middle', 'ink')
    section.deviation([11, 0])

    const elevation = new Sheet('1:200')

    elevation.line([-19, 0], [19, 0], 'line').rect(-15, 0, 15, 6.6, 'cut').rect(-10, 6.6, 2, 7.2, 'line')

    for (const h of range(0.6, 6, 0.6)) elevation.line([-15, h], [15, h], 'thin')

    elevation.rect(9, 0, 11.4, 2.8, 'line')
    levels(elevation, 20.5, [0, 6.6, 7.2])
    elevation.dim([-15, 0], [15, 0], -2).dim([15, 0], [15, 6.6], 2.6)
    elevation.note([0, -3.4], 'North elevation / board-marked concrete', 'middle')

    const axo = new Sheet('NTS')

    isoBox(axo, -15, -10, 15, 10, 0, 6.6, 'cut')
    isoBox(axo, -10, -2.6, 2, -1.4, 6.6, 7.2, 'accent')

    for (const h of range(0.6, 6, 0.6)) {
        axo.line(iso(-15, h, 10), iso(15, h, 10), 'thin')
        axo.line(iso(15, h, -10), iso(15, h, 10), 'thin')
    }

    axo.note(iso(0, 11, 0), 'Concrete pools under one roof light', 'middle', 'ink')

    return { plan: plan.drawing, section: section.drawing, elevation: elevation.drawing, axonometric: axo.drawing }
}

/* ---------------------------------------------------------------- pavilion */

const pavilion = (): DrawingSet => {
    const plan = new Sheet('1:200')

    plan.rect(-12, -5, 12, 5, 'hidden')

    for (const x of [-9, -3, 3]) for (const y of [-3, 3]) plan.rect(x - 0.15, y - 0.15, x + 0.15, y + 0.15, 'cut')

    plan.rect(-8, -3, 2, 3, 'line')

    for (const x of range(-7, 1, 1)) plan.line([x, -3], [x, -2.8], 'thin').line([x, 3], [x, 2.8], 'thin')

    plan.rect(10, -5, 12, 5, 'cut').rect(12, -4, 24, 4, 'line').line([12, 0], [24, 0], 'hidden')
    plan.poly([[13.5, -9], [13.9, -6], [13.4, -3], [13.8, 0], [13.4, 3], [13.9, 6], [13.5, 9]], 'thin')
    gridX(plan, [-9, -3, 3], -7.5, 8)
    gridY(plan, [-3, 3], -17.5, 13)
    plan.dim([-12, -5], [12, -5], -2.6).dim([3, 5], [12, 5], 1.6).dim([-12, -5], [-12, 5], -2)
    plan.note([-3, 0], 'Waiting room', 'middle', 'ink').note([18, 5.4], 'Bridge deck', 'middle').note([16, -8], 'River', 'middle')
    plan.north([22, 8])

    const section = new Sheet('1:150')

    section.line([-16, 0], [12, 0], 'thin').rect(10, -2, 12, 1.5, 'cut').rect(12, 1, 26, 1.5, 'cut')
    section.rect(-12, 4.2, 12, 4.35, 'cut')

    for (const x of [-9, -3, 3]) section.line([x, 0], [x, 4.2], 'cut')

    section.line([-8, 0], [-8, 4.2], 'line').line([2, 0], [2, 4.2], 'line').line([-8, 2.7], [2, 2.7], 'thin')
    section.poly([[12, -1.2], [14, -1.0], [16, -1.3], [18, -1.1], [20, -1.3]], 'thin')
    levels(section, 27.5, [0, 1.5, 4.2])
    section.dim([3, 4.35], [12, 4.35], 1.2, '9 000').dim([-12, 0], [-12, 4.2], -2)
    section.note([-3, 1.4], 'Waiting room', 'middle').note([19, 2.4], 'Bridge deck', 'middle').note([12, 5.6], 'Roof plate tip', 'middle', 'ink')
    section.deviation([12, 4.2])

    const elevation = new Sheet('1:150')

    elevation.line([-16, 0], [12, 0], 'line').rect(-12, 4.2, 12, 4.35, 'cut')

    for (const x of [-9, -3, 3]) elevation.line([x, 0], [x, 4.2], 'cut')

    elevation.rect(-8, 0, 2, 4.2, 'line')

    for (const x of range(-7, 1, 1)) elevation.line([x, 0], [x, 4.2], 'thin')

    elevation.rect(12, 1, 26, 1.5, 'line')
    levels(elevation, 27.5, [0, 4.2])
    elevation.dim([-12, 0], [12, 0], -1.8)
    elevation.note([0, -3], 'Quay elevation / columns 200 diameter', 'middle')

    const axo = new Sheet('NTS')

    isoBox(axo, -12, -5, 12, 5, 4.2, 4.35, 'cut')

    for (const x of [-9, -3, 3]) for (const z of [-3, 3]) axo.line(iso(x, 0, z), iso(x, 4.2, z), 'line')

    isoBox(axo, -8, -3, 2, 3, 0, 4.2, 'thin')
    isoBox(axo, 12, -4, 24, 4, 1, 1.5, 'line')
    axo.note(iso(0, 7, 0), 'Steel roof plate on six columns', 'middle', 'ink')

    return { plan: plan.drawing, section: section.drawing, elevation: elevation.drawing, axonometric: axo.drawing }
}

/* ----------------------------------------------------------------- station */

const BUILD_FRONT = 4.8

const arch = (x: number): number => 14 * (1 - (x / 20) ** 2)

const station = (): DrawingSet => {
    const plan = new Sheet('1:500')

    plan.rect(-30, -20, 30, 20, 'cut')

    for (const c of [-15, -5, 5, 15]) plan.rect(-28, c - 3, 28, c + 3, 'line')

    for (const t of [-10, 0, 10]) plan.line([-34, t - 0.72], [34, t - 0.72], 'thin').line([-34, t + 0.72], [34, t + 0.72], 'thin')

    diagonals(plan, -30, -20, 30, 20, 5.6, 1, 'thin', BUILD_FRONT, 'hidden')
    diagonals(plan, -30, -20, 30, 20, 5.6, -1, 'thin', BUILD_FRONT, 'hidden')
    plan.line([BUILD_FRONT, -21.5], [BUILD_FRONT, 21.5], 'accent')
    gridX(plan, range(-30, 30, 10), -23, 24)
    gridY(plan, [-20, 0, 20], -35, 31)
    plan.dim([-30, -20], [30, -20], -4.6).dim([30, -20], [30, 20], 7.4)
    plan.note([BUILD_FRONT + 0.8, 21.2], 'Build front / 58%', 'start', 'signal').note([-16, -15], 'Platform 1', 'middle').note([-16, 15], 'Platform 4', 'middle')
    plan.north([42, 20])

    const section = new Sheet('1:300')
    const outer: Array<Pt> = []
    const inner: Array<Pt> = []

    for (let x = -20; x <= 20.001; x += 1) {
        outer.push([x, arch(x)])
        inner.push([x * 0.98, arch(x) - 0.45])
    }

    section.line([-24, 0], [24, 0], 'thin').poly(outer, 'cut').poly(inner, 'line')

    for (const c of [-15, -5, 5, 15]) section.rect(c - 3, 0, c + 3, 1.1, 'cut')

    for (const x of range(-17.5, 17.5, 2.5)) section.line([x, arch(x) - 0.45], [x, arch(x)], 'thin')

    levels(section, 27.5, [0, 1.1, 14])
    section.dim([-20, 0], [20, 0], -2.4).dim([20, 0], [20, 14], 4.6, '14 000')
    section.note([0, 4], 'Four platforms under one shell', 'middle').note([7.5, 13.4], 'Grid line 14', 'start', 'ink')
    section.deviation([7.5, arch(7.5)])

    const elevation = new Sheet('1:500')

    elevation.line([-34, 0], [34, 0], 'line').poly([[-30, 0], [-30, 14], [30, 14], [30, 0]], 'cut')
    diagonals(elevation, -30, 0, 30, 14, 4, 1, 'thin', BUILD_FRONT, 'hidden')
    diagonals(elevation, -30, 0, 30, 14, 4, -1, 'thin', BUILD_FRONT, 'hidden')
    elevation.line([BUILD_FRONT, -1], [BUILD_FRONT, 15], 'accent')
    levels(elevation, 33, [0, 14])
    elevation.dim([-30, 0], [30, 0], -2.4)
    elevation.note([BUILD_FRONT + 0.8, 15.4], 'Build front', 'start', 'signal').note([0, -4.6], 'Long elevation / timber gridshell', 'middle')

    const axo = new Sheet('NTS')

    for (const x of range(-30, 30, 5)) {
        const points: Array<Pt> = []

        for (let z = -20; z <= 20.001; z += 2) points.push(iso(x, arch(z), z))

        axo.poly(points, x <= BUILD_FRONT ? 'line' : 'hidden')
    }

    for (const z of [-20, -10, 0, 10, 20]) axo.line(iso(-30, arch(z), z), iso(BUILD_FRONT, arch(z), z), 'line').line(iso(BUILD_FRONT, arch(z), z), iso(30, arch(z), z), 'hidden')

    for (const c of [-15, -5, 5, 15]) isoBox(axo, -28, c - 3, 28, c + 3, 0, 1.1, 'thin')

    axo.note(iso(-12, 18, 0), 'Timber gridshell over four platforms', 'middle', 'ink')

    return { plan: plan.drawing, section: section.drawing, elevation: elevation.drawing, axonometric: axo.drawing }
}

/* ----------------------------------------------------------------- generic */

const generic = (): DrawingSet => {
    const plan = new Sheet('1:300')

    plan.rect(-15, -10, 15, 10, 'cut').rect(-14.7, -9.7, 14.7, 9.7, 'line')
    gridX(plan, range(-15, 15, 7.5), -12.5, 13.5)
    gridY(plan, [-10, 0, 10], -18, 16)
    stair(plan, -14.7, -9.7, -11, -5)
    plan.dim([-15, -10], [15, -10], -3).dim([15, -10], [15, 10], 2.6)
    plan.north([20, 10])

    const section = new Sheet('1:300')

    section.line([-19, 0], [19, 0], 'thin').poly([[-15, 0], [-15, 12], [15, 12], [15, 0]], 'cut')

    for (const h of [4, 8]) section.rect(-15, h - 0.3, 15, h, 'cut')

    levels(section, 17.5, [0, 4, 8, 12])
    section.dim([-15, 0], [-15, 12], -2.6)
    section.deviation([0, 8])

    const elevation = new Sheet('1:300')

    elevation.line([-19, 0], [19, 0], 'line').rect(-15, 0, 15, 12, 'cut')

    for (const h of [4, 8]) elevation.line([-15, h], [15, h], 'line')

    levels(elevation, 17.5, [0, 12])

    const axo = new Sheet('NTS')

    isoBox(axo, -15, -10, 15, 10, 0, 12, 'cut')

    return { plan: plan.drawing, section: section.drawing, elevation: elevation.drawing, axonometric: axo.drawing }
}

const BUILDERS: Record<string, () => DrawingSet> = {
    library,
    housing,
    hall,
    bathhouse,
    pavilion,
    station
}

const cache = new Map<string, DrawingSet>()

/**
 * The drawing set for a building, keyed by the same shape key its point cloud uses.
 */
export const getDrawingSet = (shape: string): DrawingSet => {
    const key = BUILDERS[shape] ? shape : 'generic'
    const hit = cache.get(key)

    if (hit) return hit

    const set = (BUILDERS[key] ?? generic)()

    cache.set(key, set)

    return set
}

export { mm }
